/**
 * Value Metric Display Component
 * 
 * Unified display for value ratio and entropy score metrics.
 * Used across header, dashboard, and card components for consistency.
 * 
 * Value Ratio: value generated / cost to generate
 *   - < 1 = loss (red)
 *   - = 1 = break-even (yellow)  
 *   - > 1 = profit (green)
 * 
 * Entropy Score: 1-10 scale measuring coordination loss (lower = better)
 *   - 1-2 = Excellent (tightly coordinated)
 *   - 3-4 = Good
 *   - 5-6 = Fair
 *   - 7-8 = Poor
 *   - 9-10 = Critical (high coordination loss)
 * 
 * @see docs/KRYSAIS_CONTROL_GLOSSARY.md for terminology definitions
 */

import React from 'react';
import { Tooltip } from 'antd';
import { 
  RiseOutlined, 
  FallOutlined, 
  MinusOutlined,
  ThunderboltOutlined 
} from '@ant-design/icons';
import styles from './ValueMetricDisplay.module.css';

/** Value type determines ratio thresholds for color coding */
export type ValueType = 'business' | 'stakeholder' | 'customer';

export interface ValueMetricDisplayProps {
  /** Value ratio (value generated / cost). Thresholds vary by type */
  valueRatio: number;
  /** Entropy score on 1-10 scale (1=best, 10=worst) - measures coordination loss */
  entropyScore?: number;
  /** @deprecated Use entropyScore instead. Kept for backward compatibility. */
  assessmentRank?: number;
  /** Type of value - determines ratio thresholds for color coding */
  valueType?: ValueType;
  /** Display variant */
  variant?: 'compact' | 'standard' | 'detailed';
  /** Whether to show text labels */
  showLabels?: boolean;
  /** Optional click handler for entropy badge */
  onEntropyClick?: () => void;
  /** @deprecated Use onEntropyClick instead */
  onRankClick?: () => void;
  /** Optional label for the metric type */
  label?: string;
  /** Custom class name */
  className?: string;
}

/** 
 * Get entropy level info based on 1-10 scale (lower = better)
 * Entropy measures coordination loss between intent and execution.
 * @see docs/KRYSAIS_CONTROL_GLOSSARY.md
 */
export const getEntropyInfo = (score: number): { 
  level: string; 
  color: string; 
  nextLevel: string | null;
  nextScore: number | null;
} => {
  // Lower is better for entropy (1 = tightly coordinated, 10 = chaotic)
  if (score <= 2) return { 
    level: 'Excellent', 
    color: '#10b981', 
    nextLevel: score === 1 ? null : 'Excellent',
    nextScore: score === 1 ? null : 1
  };
  if (score <= 4) return { 
    level: 'Good', 
    color: '#3b82f6', 
    nextLevel: 'Excellent',
    nextScore: 2
  };
  if (score <= 6) return { 
    level: 'Fair', 
    color: '#f59e0b', 
    nextLevel: 'Good',
    nextScore: 4
  };
  if (score <= 8) return { 
    level: 'Poor', 
    color: '#ef4444', 
    nextLevel: 'Fair',
    nextScore: 6
  };
  return { 
    level: 'Critical', 
    color: '#dc2626', 
    nextLevel: 'Poor',
    nextScore: 8
  };
};

/** @deprecated Use getEntropyInfo instead. Kept for backward compatibility. */
export const getRankInfo = (rank: number) => {
  // Convert old rank (higher=better) to entropy (lower=better) for backward compat
  const entropyScore = 11 - rank; // rank 10 -> entropy 1, rank 1 -> entropy 10
  return getEntropyInfo(entropyScore);
};

/**
 * Ratio thresholds by value type:
 * - Business: 1.0 = break-even, > 1 = good
 * - Stakeholder: 2.0 = good threshold
 * - Customer: 4.0 = good threshold
 */
const RATIO_THRESHOLDS: Record<ValueType, { good: number; fair: number }> = {
  business: { good: 1.1, fair: 0.95 },
  stakeholder: { good: 2.0, fair: 1.5 },
  customer: { good: 4.0, fair: 2.5 },
};

/** Get color for value ratio based on value type */
export const getRatioColor = (ratio: number, valueType: ValueType = 'business'): string => {
  const thresholds = RATIO_THRESHOLDS[valueType];
  if (ratio >= thresholds.good) return '#10b981'; // Green - good
  if (ratio >= thresholds.fair) return '#f59e0b'; // Yellow - fair
  return '#ef4444'; // Red - needs attention
};

/** Get ratio status text based on value type */
const getRatioStatusByType = (ratio: number, valueType: ValueType): string => {
  const thresholds = RATIO_THRESHOLDS[valueType];
  if (ratio >= thresholds.good * 1.2) return 'Excellent';
  if (ratio >= thresholds.good) return 'Good';
  if (ratio >= thresholds.fair) return 'Fair';
  if (ratio >= thresholds.fair * 0.7) return 'Below Target';
  return 'Needs Attention';
};

/** Get status icon for value ratio */
const RatioStatusIcon: React.FC<{ ratio: number; valueType: ValueType }> = ({ ratio, valueType }) => {
  const thresholds = RATIO_THRESHOLDS[valueType];
  if (ratio >= thresholds.good) {
    return <RiseOutlined className={styles.ratioIcon} style={{ color: '#10b981' }} />;
  }
  if (ratio < thresholds.fair) {
    return <FallOutlined className={styles.ratioIcon} style={{ color: '#ef4444' }} />;
  }
  return <MinusOutlined className={styles.ratioIcon} style={{ color: '#f59e0b' }} />;
};

export const ValueMetricDisplay: React.FC<ValueMetricDisplayProps> = ({
  valueRatio,
  entropyScore,
  assessmentRank, // deprecated, for backward compat
  valueType = 'business',
  variant = 'standard',
  showLabels = true,
  onEntropyClick,
  onRankClick, // deprecated, for backward compat
  label,
  className
}) => {
  // Support both new entropyScore and deprecated assessmentRank
  // If entropyScore provided, use it; otherwise convert from assessmentRank
  const effectiveEntropyScore = entropyScore ?? (assessmentRank ? 11 - assessmentRank : 5);
  const entropyInfo = getEntropyInfo(effectiveEntropyScore);
  const ratioColor = getRatioColor(valueRatio, valueType);
  const ratioStatus = getRatioStatusByType(valueRatio, valueType);
  const handleClick = onEntropyClick ?? onRankClick;
  
  const containerClasses = [
    styles.container,
    styles[variant],
    className
  ].filter(Boolean).join(' ');

  const entropyTooltip = entropyInfo.nextLevel 
    ? `${entropyInfo.level} • Target: Score ${entropyInfo.nextScore} (${entropyInfo.nextLevel})`
    : `${entropyInfo.level} • Optimal coordination achieved!`;

  return (
    <div className={containerClasses}>
      {label && <div className={styles.metricLabel}>{label}</div>}
      
      <div className={styles.metricRow}>
        {/* Value Ratio - Left Side */}
        <div className={styles.ratioSection}>
          {showLabels && variant !== 'compact' && (
            <span className={styles.sectionLabel}>Value Ratio</span>
          )}
          <div className={styles.ratioDisplay}>
            <span 
              className={styles.ratioValue}
              style={{ color: ratioColor }}
            >
              {valueRatio.toFixed(2)}
            </span>
            <Tooltip title={ratioStatus}>
              <span className={styles.ratioIndicator}>
                <RatioStatusIcon ratio={valueRatio} valueType={valueType} />
              </span>
            </Tooltip>
          </div>
          {variant === 'detailed' && (
            <span className={styles.ratioHint} style={{ color: ratioColor }}>
              {ratioStatus}
            </span>
          )}
        </div>

        {/* Entropy Score Badge - Right Side */}
        <div className={styles.rankSection}>
          {showLabels && variant !== 'compact' && (
            <span className={styles.sectionLabel}>Entropy</span>
          )}
          <Tooltip title={entropyTooltip}>
            <button
              className={styles.rankBadge}
              style={{ 
                '--rank-color': entropyInfo.color,
                '--rank-glow': `${entropyInfo.color}40`
              } as React.CSSProperties}
              onClick={handleClick}
              disabled={!handleClick}
              type="button"
            >
              <span className={styles.rankNumber}>{effectiveEntropyScore}</span>
              {variant !== 'compact' && (
                <ThunderboltOutlined className={styles.rankIcon} />
              )}
            </button>
          </Tooltip>
          {variant === 'detailed' && (
            <span className={styles.rankLevel} style={{ color: entropyInfo.color }}>
              {entropyInfo.level}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ValueMetricDisplay;
